import {
  ACTION_GROUP_NODE_NAME,
  DOCUMENT_GROUP_NODE_NAME,
  RULE_GROUP_HOLDER_NODE_NAME,
  TRIGGER_GROUP_NODE_NAME,
} from "@repo/shared/migration-types";
import { isGroupNode } from "@repo/shared/workflow";
import type { NodeData, Workflow } from "@repo/shared/workflow";
import type {
  Edge as ReactFlowEdge,
  Node as ReactFlowNode,
} from "@xyflow/react";
import _ from "lodash";
import { useMemo } from "react";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

export interface EditorState {
  workflow: Workflow | null;
  selectedNodeId: string | null;
  collapsed: Record<string, boolean>;

  init: (workflow: Workflow) => void;
  selectNode: (nodeId: string | null) => void;
  setNodeCollapsed: (nodeId: string, collapsed: boolean) => void;
}

export const useEditorState = create<EditorState>()(
  immer((set) => ({
    workflow: null as Workflow | null,
    selectedNodeId: null as string | null,
    collapsed: {} as Record<string, boolean>,
    init: (workflow) => {
      set((state) => {
        state.workflow = workflow;
        state.collapsed = {};
        state.selectedNodeId = null;

        for (const node of workflow.nodes) {
          if (isGroupNode(node)) {
            state.collapsed[node.id] = true;
          }
        }
      });
    },
    selectNode: (nodeId) => {
      set((state) => {
        state.selectedNodeId = nodeId;
      });
    },
    setNodeCollapsed: (nodeId, collapsed) => {
      set((state) => {
        state.collapsed[nodeId] = collapsed;

        const node = state.workflow?.nodes.find((n) => n.id === nodeId);
        if (!node) return;

        // if we're setting collapse state of a group node, we need to update the collapse state of all its children
        if (isGroupNode(node)) {
          for (const childId of node.dependencyIds) {
            const child = state.workflow?.nodes.find((n) => n.id === childId);

            // Don't update the collapsed state of a child if it's a group node
            if (!child || isGroupNode(child)) continue;

            state.collapsed[childId] = collapsed;
          }
        }
      });
    },
  })),
);

export const useEditorNodes = () => {
  const workflow = useEditorState((state) => state.workflow);
  const collapsed = useEditorState((state) => state.collapsed);

  return useMemo(() => {
    if (!workflow) return {};

    const nodeMap = _.keyBy(workflow.nodes, "id");

    const childrenOfGroupNodes = new Set(
      workflow.nodes.filter(isGroupNode).flatMap((n) => n.dependencyIds),
    );
    const nodeParents: Record<string, string> = {};
    for (const node of workflow.nodes) {
      if (isGroupNode(node)) {
        for (const childId of node.dependencyIds) {
          nodeParents[childId] = node.id;
        }
      }
    }

    const isNodeHidden = (nodeId: string) => {
      // Take the last stored collapsed state or default to true if a child
      const parentId = nodeParents[nodeId];
      if (parentId) {
        return collapsed[parentId] ?? childrenOfGroupNodes.has(nodeId);
      }

      return childrenOfGroupNodes.has(nodeId);
    };

    // if group: get visible child dependency ids
    // if not group: get first visible parent

    const getFirstVisibleNodeOrParent = (nodeId: string) => {
      let ret = nodeId;
      while (ret != null && isNodeHidden(ret)) {
        ret = nodeParents[ret];
      }

      return ret;
    };

    // Get the dependency ids of all the leaf nodes of a node
    // Leaf nodes are all non-group nodes in a group
    const getLeafDependencyIds = (nodeId: string) => {
      const ret = new Set<string>();
      const stack = [nodeId];

      while (stack.length > 0) {
        const id = stack.pop();
        if (!id) continue;
        const node = nodeMap[id];

        if (isGroupNode(node)) {
          stack.push(...node.dependencyIds);
        } else {
          ret.add(id);
        }
      }

      const leaves = Array.from(ret);

      return _.uniq(leaves.flatMap((id) => nodeMap[id].dependencyIds));
    };

    /**
     * Given that any node can be in a group and groups can be nested,
     * and that groups can be expanded or collapsed (hiding all children),
     * we need to get the closest non-hidden dependency id for each dependency id
     */
    const getVisibleDependencyIds = (node: NodeData): string[] => {
      const ret: Set<string> = new Set();

      if (isGroupNode(node) && collapsed[node.id]) {
        for (const childId of getLeafDependencyIds(node.id)) {
          ret.add(getFirstVisibleNodeOrParent(childId));
        }
      } else {
        for (const id of node.dependencyIds) {
          ret.add(getFirstVisibleNodeOrParent(id));
        }
      }

      return Array.from(ret);
    };

    const nodeDisplay: NodeDisplay[] = [];
    for (const node of workflow.nodes) {
      const name = getDisplayName(node);
      const internalNode = [
        RULE_GROUP_HOLDER_NODE_NAME,
        DOCUMENT_GROUP_NODE_NAME,
        ACTION_GROUP_NODE_NAME,
        TRIGGER_GROUP_NODE_NAME,
      ].includes(node.name);

      const hide = isNodeHidden(node.id);

      nodeDisplay.push({
        node: node,
        displayName: name,
        isInternalNode: internalNode,
        hide,
        displayDependencyIds: getVisibleDependencyIds(node),
      });
    }

    return _.keyBy(nodeDisplay, "node.id");
  }, [workflow, collapsed]);
};

// Can't declare 'group' as a type because its a ReactFlow internal type
export type NodeType = "base" | "foundation-group";

export type NodeDisplay<T extends NodeData = NodeData> = {
  node: T;
  displayName: string;
  isInternalNode: boolean;
  hide?: boolean;
  /**
   * Include's the node's dependency ids and the dependency ids of the node's children
   * The children's dependency ids need to appear as if coming from the group node
   * The arrows will need to come from the group instead of each child
   */
  displayDependencyIds: string[];
};

export type RFNode = ReactFlowNode<NodeDisplay, NodeType>;
export type RFEdge = ReactFlowEdge;

const getDisplayName = (node: NodeData) => {
  if (node.name === ACTION_GROUP_NODE_NAME) return "Actions";
  if (node.name === DOCUMENT_GROUP_NODE_NAME) return "Documents";
  if (node.name === TRIGGER_GROUP_NODE_NAME) return "Triggers";
  if (node.name === RULE_GROUP_HOLDER_NODE_NAME) return "Rules";

  return node.name;
};
